// This optional code is used to register a service worker.
// register() is not called by default.

// This lets the app load faster on subsequent visits in production, and gives
// it offline capabilities. However, it also means that developers (and users)
// will only see deployed updates on subsequent visits to a page, after all the
// existing tabs open on the page have been closed, since previously cached
// resources are updated in the background.

// To learn more about the benefits of this model and instructions on how to
// opt-in, read https://cra.link/PWA

const isLocalhost = Boolean(
  window.location.hostname === "localhost" ||
  // [::1] is the IPv6 localhost address.
  window.location.hostname === "[::1]" ||
  // 127.0.0.0/8 are considered localhost for IPv4.
  window.location.hostname.match(
    /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/,
  ),
);

function isStaleRegistrationError(error) {
  const message = error instanceof Error ? error.message : String(error);
  return (
    message.includes("uninstalled") ||
    message.includes("no longer") ||
    message.includes("not, or is no longer")
  );
}

function updateRegistration(registration, context) {
  try {
    registration.update().catch((error) => {
      if (!isStaleRegistrationError(error)) {
        console.error(`[SW] Error during ${context} update check:`, error);
      }
    });
  } catch (error) {
    if (!isStaleRegistrationError(error)) {
      console.error(`[SW] Error during ${context} update check:`, error);
    }
  }
}

export function register(config) {
  if (import.meta.env.PROD && "serviceWorker" in navigator) {
    console.log("[SW] Environment check passed, proceeding with registration");
    const publicUrl = new URL(import.meta.env.BASE_URL, window.location.href);
    if (publicUrl.origin !== window.location.origin) {
      console.error(
        "[SW] Public URL is on different origin than window location",
      );
      return;
    }

    window.addEventListener("load", () => {
      const swUrl = `${import.meta.env.BASE_URL}service-worker.js`;
      console.log("[SW] Service worker URL:", swUrl);

      if (isLocalhost) {
        console.log("[SW] Running in localhost environment");
        checkValidServiceWorker(swUrl, config);
        navigator.serviceWorker.ready.then(() => {
          console.log("[SW] Service worker is ready on localhost");
        });
      } else {
        console.log("[SW] Running in production environment");
        registerValidSW(swUrl, config);
      }
    });

    // Add periodic check for updates with better logging
    setInterval(
      () => {
        if (navigator.serviceWorker.controller) {
          console.log("[SW] Checking for updates...");
          navigator.serviceWorker.controller.postMessage({
            type: "SKIP_WAITING",
          });
          navigator.serviceWorker.getRegistration().then((registration) => {
            if (registration) {
              console.log(
                "[SW] Found existing registration, checking for updates",
              );
              updateRegistration(registration, "periodic");
            } else {
              console.warn("[SW] No registration found during update check");
            }
          });
        } else {
          console.warn("[SW] No controller found during update check");
        }
      },
      1000 * 60 * 30,
    ); // Check every 30 minutes
  } else {
    console.log(
      "[SW] Service worker registration skipped - not in production or not supported",
    );
  }
}

function registerValidSW(swUrl, config) {
  console.log("[SW] Starting registration process for:", swUrl);
  navigator.serviceWorker
    .register(swUrl, {
      updateViaCache: "none",
      immediate: true,
    })
    .then((registration) => {
      console.log("[SW] Registration successful:", registration);
      registration.addEventListener("updatefound", () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) {
          console.warn("[SW] No installing worker found");
          return;
        }

        console.log("[SW] New service worker is installing");
        installingWorker.addEventListener("statechange", () => {
          console.log(
            "[SW] Service worker state changed to:",
            installingWorker.state,
          );
          if (installingWorker.state === "installed") {
            if (navigator.serviceWorker.controller) {
              console.log("[SW] New content is available");
              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }
            } else {
              console.log("[SW] Content is cached for offline use");
              if (config && config.onSuccess) {
                config.onSuccess(registration);
              }
            }
          }
        });
      });

      // Check for updates, but only if registration is still active
      // Don't check immediately after registration to avoid conflicts
      setTimeout(() => {
        navigator.serviceWorker
          .getRegistration(swUrl)
          .then((currentRegistration) => {
            if (currentRegistration) {
              updateRegistration(currentRegistration, "initial");
            }
          })
          .catch((error) => {
            if (!isStaleRegistrationError(error)) {
              console.error("[SW] Error getting registration:", error);
            }
          });
      }, 1000);
    })
    .catch((error) => {
      console.error("[SW] Registration failed:", error);
    });
}

function checkValidServiceWorker(swUrl, config) {
  console.log("[SW] Validating service worker for:", swUrl);
  // Check if the service worker can be found. If it can't reload the page.
  fetch(swUrl, {
    headers: { "Service-Worker": "script" },
  })
    .then((response) => {
      console.log("[SW] Service worker fetch response:", response);
      // Ensure service worker exists, and that we really are getting a JS file.
      const contentType = response.headers.get("content-type");
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf("javascript") === -1)
      ) {
        console.error(
          "[SW] Invalid service worker found. Status:",
          response.status,
          "Content-Type:",
          contentType,
        );
        // No service worker found. Probably a different app. Reload the page.
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            console.log(
              "[SW] Unregistered invalid service worker, reloading page",
            );
            window.location.reload();
          });
        });
      } else {
        console.log(
          "[SW] Valid service worker found, proceeding with registration",
        );
        registerValidSW(swUrl, config);
      }
    })
    .catch((error) => {
      console.error("[SW] Error during service worker validation:", error);
      console.log(
        "[SW] No internet connection found. App is running in offline mode.",
      );
    });
}

export function unregister() {
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        console.error(error.message);
      });
  }
}
